import React, { useState, useRef, useEffect, useCallback } from "react";
import Menu from "../../../../../components/layout/menu/index";
import '../../../../../../src/index.css'
import FilterInput from "../../../../../components/filters/filterInput";
import DynamicTable from "../../../../../components/tables/oc/provider/dynamicTable";
import { HistoricOCS, downloadExcelHistoricOC } from "../../../../../client/purchaseOrder";
import DateFilterButton from "../../../../../components/filters/dateFilterButton";
import Pagination from "../../../../../components/shared/pagination";
import ContentLoader from 'react-content-loader';
import { format} from 'date-fns';
import ResetFilters from "../../../../../components/filters/resetFilters";
import DownloadDocumentButton from "../../../../../components/shared/downloadDocument";
import ModalAlertWarning from "../../../../../components/shared/modals/modalAlertWarning";
const OrderSkeleton = () => (
  <div className="w-full mt-3">
    <div className="flex w-full pb-1 px-1 border-b-[1px] border-[#f3f3f3] justify-between gap-2">
      <ContentLoader 
        speed={2}
        width={100}
        height={20}
        viewBox="0 0 100 20"
        backgroundColor="#f3f3f3"
        foregroundColor="#ecebeb"
      >
        <rect x="0" y="0" rx="3" ry="3" width="100" height="16" />
      </ContentLoader>
      <ContentLoader 
        speed={2}
        width={18}
        height={18}
        viewBox="0 0 18 18"
        backgroundColor="#f3f3f3"
        foregroundColor="#ecebeb"
      >
        <rect x="0" y="0" rx="3" ry="3" width="18" height="18" />
      </ContentLoader>
    </div>
  </div>
);

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export default function HistoricOCAdmin() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const [expandedOrder, setExpandedOrder] = useState(null);
  const [expandedPosition, setExpandedPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false); 
  const [error, setError] = useState(null);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(30);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [allOrders, setAllOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]); 

  const defaultStartDate = format(new Date(new Date().setMonth(new Date().getMonth() - 12)), 'yyyy-MM-dd');
  const defaultEndDate = format(new Date(), 'yyyy-MM-dd');

  const [filters, setFilters] = useState({
    emissionDateFrom: defaultStartDate,
    emissionDateTo: defaultEndDate,
    searchTerm: ''
  });

  const contentRefs = useRef({});
  const positionContentRefs = useRef({});

  const fetchHistoricOCS = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await HistoricOCS({
        emissionDateFrom: filters.emissionDateFrom || undefined,
        emissionDateTo: filters.emissionDateTo || undefined,
        search: searchTerm.trim() !== '' ? searchTerm : undefined,
        page,
        pageSize
      });
      
      if (response.data) {
        setAllOrders(response.data.data || []);
        setFilteredOrders(response.data.data || []); 
        setTotalRecords(response.data.totalCount || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        setAllOrders([]);
        setFilteredOrders([]);
        console.error("Unexpected data format:", response.data);
      }
    } catch (error) {
      console.error("Error fetching historic OCS:", error);
      setError(error);
      setAllOrders([]);
      setFilteredOrders([]);
    } finally {
      setLoading(false);
    }
  }, [filters.emissionDateFrom, filters.emissionDateTo, searchTerm, pageSize]);

  useEffect(() => {
    const handleOCsUpdated = () => {
      fetchHistoricOCS(currentPage);
    };

    window.addEventListener('ocs-updated', handleOCsUpdated);
    
    return () => {
      window.removeEventListener('ocs-updated', handleOCsUpdated);
    };
  }, [fetchHistoricOCS, currentPage]);

  useEffect(() => {
    fetchHistoricOCS(currentPage);
  }, [fetchHistoricOCS, currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  };

  const toggleOrder = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
    setExpandedPosition(null);
  };

  const togglePosition = (positionId) => {
    setExpandedPosition(expandedPosition === positionId ? null : positionId);
  };

  const tableConfig = {
    headers: [
      "#", 
      "POS", 
      "Fecha de estado", 
      "Hora de estado",
      "Estado", 
      "Descripción", 
      "Cantidad", 
      "UM", 
      "Precio Unit.", 
      "Importe Total", 
      "Moneda", 
      "Solped", 
      "POS Solped", 
      "Estado de Solped", 
      "Acción correctiva",
      "Motivo de rechazo",
      "Descripción de rechazo", 
      "Comprador", 
      "Fecha de entrega"
    ],
    accessors: [
      "id",
      "pos",
      "statusDate",
      "statusTime",
      "status",
      "description",
      "cant",
      "unit_weight",
      "price_per_unit",
      "net_amount",
      "coin",
      "solped",
      "posSolped",
      "statusSolped",
      "correctiveAction",
      "reasonText",
      "descriptionStatus",
      "comprador",
      "delivery_Date"
    ],
    widths: [
      "50px", //#
      "111px",//POS
      "112px",//fecha de estado
      "112px",//hora de estado
      "115px",//estado
      "232px",//descripcion
      "112px",//cantidad
      "112px",//UM
      "112px", //Precio unit.
      "112px", //Importe total
      "112px", //Moneda
      "175px", //Solped
      "111px", //POS solped
      "219px", //Estado de solped
      "232px", //Acción correctiva
      "232px", //Motivo de rechazo
      "232px", //Descripción de rechazo
      "232px", //Comprador
      "112px" //Fecha de entrega
    ]
  };

  const statusColors = {
    "Nuevo": "#00A3E0",
    "Aprobado": "#30EA06",
    "Vencida": "#FF1C1C",
    "Corregido": "#FF6A00",
    "Pendientes de entrega": "#FFB81C",
    "Rechazado": "#E44949",
    "Entregado": '#286AD4',
    "Creado": "#77A042"
  };

  useEffect(() => {
    if (expandedPosition !== null) {
      const newHeight = positionContentRefs.current[expandedPosition]?.scrollHeight || 0;
      positionContentRefs.current[expandedPosition].style.height = `${newHeight}px`;
    }
  }, [expandedPosition]);

  const transformPositionData = (position, order, index) => {

    const formatDecimal = (value) => {
      if (value === null || value === undefined) return '';
      const num = parseFloat(value);
      if (isNaN(num)) return '';
      
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };
    
    const baseData = {
      id: 1,
      pos: position.position.number,
      description: position.description,
      cant: position.cant,
      unit_weight: position.unit_weight,
      price_per_unit: formatDecimal(position.price_per_unit),
      net_amount: formatDecimal(position.net_amount),
      coin: position.coin,
      delivery_Date: position.delivery_Date,
      comprador: order.user?.name === "Usuario no asignado" 
        ? order.user?.user_code || '' 
        : order.user?.name || '',
      statusSolped: position.pending_release || '',
      solped: position.solped_number || '',
      posSolped: position.solped_position || '',
    };

    if (!position.status || position.status.length === 0) {
      return [{
        ...baseData,
      created_At: "", // Fecha de creación
      created_Time: "", // Hora de creación
        statusDate: ""
      }];
    }

    return position.status.map((status, statusIndex) => ({
      ...baseData,
      id: statusIndex + 1,
      statusTime: status.statusTime,
      status: status.status.name,
      statusDate: status.statusDate,
      correctiveAction: status.corrective_action || "",
      reasonText: status.reasonText,
      descriptionStatus: status.description,
    }));
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setFilters({
      emissionDateFrom: defaultStartDate,
      emissionDateTo: defaultEndDate
    });
    setCurrentPage(1);
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      
      const response = await downloadExcelHistoricOC({
        emissionDateFrom: filters.emissionDateFrom,
        emissionDateTo: filters.emissionDateTo,
        search: searchTerm.trim() !== '' ? searchTerm : ''
      });

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `OCs_Historico_${format(new Date(), 'yyyyMMddHHmmss')}.xlsx`;
      
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          fileName = fileNameMatch[1];
        }
      }
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error("Error downloading Excel:", error);
      setErrorMessage("Error al descargar el archivo. Por favor, intente nuevamente.");
      setShowErrorModal(true);
    } finally {
      setDownloading(false);
    }
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage('');
  };


  return (
    <div>
      <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300" />
      <div className={`flex-1 pt-[67px] transition-all duration-300 ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"}  bg-[#F7F8F9] w-full z-50 min-h-screen`}>
        <div className="pt-4 pb-10 px-[3%]">
            <div className="flex flex-col items-center justify-between w-full gap-2 lg:flex-row">
                <h1 className="text-2xl md:text-[32px] text-blue font-itcbold">Histórico</h1>
                <div className="flex flex-col items-center justify-center w-full gap-3 md:justify-end md:flex-row">
                  <DownloadDocumentButton
                    text="Descargar"
                    onClick={handleDownloadExcel}
                    loading={downloading}
                    hasData={filteredOrders.length > 0}
                    disabled={downloading || filteredOrders.length === 0}
                  />
                  <FilterInput 
                    value={searchTerm}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                        if (inputValue === '' || /^[0-9]*$/.test(inputValue)) {
                          setSearchTerm(inputValue);
                          setCurrentPage(1); 
                        }
                    }}
                    placeholder="Buscar OC"
                  />
                  <div className="flex gap-3">
                  <DateFilterButton 
                    title="Fecha de emisión:"
                    activeFilters={filters}
                    onDateRangeChange={(dates) => {
                      setFilters({
                        emissionDateFrom: dates.startDate,
                        emissionDateTo: dates.endDate
                      });
                      setCurrentPage(1);
                    }}
                    position="right-0"
                  />
                  <ResetFilters
                  font="font-itcbook"
                  onClick={handleResetFilters}
                  />
                  </div>
                </div>
            </div>

          {loading ? (
            <div className="space-y-4">
              {[...Array(20)].map((_, index) => (
                <OrderSkeleton key={index} />
              ))}
            </div>
          ) : error ? (
            <div className="flex items-center justify-center w-full h-64">
              <p className="text-red-500">Error loading data: {error.message}</p>
            </div>
          ) : filteredOrders.length > 0 ? (
            filteredOrders.map(order => (
              <>
              <div key={order.id} className="">
                <button 
                    key={order.id}
                    onClick={() => toggleOrder(order.id)}
                    className="flex w-full mt-3 pb-1 items-center px-1 border-b-[1px] border-[#808080] justify-between gap-2">
                    <p className="text-[#52526C] font-itcmedium text-[14px]">
                      OC {order.order_number}
                    </p>
                    <svg  className={`transition-transform duration-200 ${expandedOrder === order.id ? 'rotate-180' : ''}`} width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.55957 6.63501H13.4404C13.7125 6.63531 13.8531 6.96517 13.6641 7.16138L9.7793 11.0461C9.37869 11.4468 8.74158 11.472 8.31152 11.1213L8.22852 11.0461L4.34375 7.16138C4.14792 6.96555 4.28665 6.6354 4.55957 6.63501Z" fill="#292D32" stroke="#6B7280"/>
                    </svg>
                </button>

                <div
                  ref={el => contentRefs.current[order.id] = el}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    expandedOrder === order.id ? 'max-h-[9999px]' : 'max-h-0'
                  }`}
                > 
                  <div className="ml-8">
                      {order.positions && order.positions.length > 0 ? (
                      order.positions.map((position, index) => (
                      <div key = {position.id}>
                        <button 
                          onClick={()=> togglePosition(position.id)}
                          className="flex w-full mt-3 pb-1 items-center px-1 border-b-[1px] border-[#808080] justify-between gap-2"
                        >
                          <p className="text-[#52526C] font-itcmedium text-[14px]">
                            POS {position.position.number}
                          </p>
                          <svg
                            className={`transition-transform duration-200 ${expandedPosition === position.id ? 'rotate-180' : ''}`} width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.55957 6.63501H13.4404C13.7125 6.63531 13.8531 6.96517 13.6641 7.16138L9.7793 11.0461C9.37869 11.4468 8.74158 11.472 8.31152 11.1213L8.22852 11.0461L4.34375 7.16138C4.14792 6.96555 4.28665 6.6354 4.55957 6.63501Z" fill="#292D32" stroke="#6B7280"/>
                          </svg>
                        </button>
                        <div
                          ref={el => positionContentRefs.current[position.id] = el}
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            expandedPosition === position.id ? 'max-h-[9999px]' : 'max-h-0'
                          }`}
                        >
                          <div className="py-2">
                            <DynamicTable 
                              data={transformPositionData(position, order, index)} 
                              config={tableConfig}
                              statusColors={statusColors}
                              showPagination={false}
                            />
                          </div>
                        </div>
                      </div>
                    ))
                  ):(
                    <div className="flex items-center justify-center w-full p-4 text-center">
                      <p className="text-gray font-itcmedium">La orden de compra no tiene posiciones.</p>
                    </div>
                  )}
                  </div>
                </div>
              </div>
              </>
            ))
          ) :(
            <div className="flex items-center justify-center w-full p-8 py-16 text-center">
              <p className="text-red font-itcmedium">No se encontraron órdenes históricas.</p>
            </div>
          )}
          {!loading && filteredOrders.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              changePage={handlePageChange}
              recordsPerPage={pageSize}
              totalRecords={totalRecords}
            />
          )}
        </div>
      </div>

      <ModalAlertWarning
        showModalAlertWarning={showErrorModal}
        closeModal={closeErrorModal}
        error={errorMessage}
        accept={closeErrorModal}
        color="#CA2D00" 
      />
    </div>
  );
}